import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { clearAllOnboardingV2Drafts } from '../components/onboarding-v2/draft'

// LAZY posthog · el paquete (~47KB gzip) sale del chunk main; index.js lo importa de
// forma dinámica SOLO con consentimiento analytics y publica el singleton en
// window.__posthog. Aquí lo consumimos sin re-importar: si no hay consentimiento el
// singleton no existe y estas capturas son no-op (telemetría OFF, comportamiento igual).
function ph() {
  const p = typeof window !== 'undefined' ? window.__posthog : null
  return p && p.__loaded ? p : null
}

// QA-E2E bug#3 · hint de routing scopeado por userId (misma familia que el draft del
// bug#2). Guarda SOLO el booleano onboarding_completed (cero PII) para resolver el
// gate al instante en usuario conocido (sin flash de loader). La verdad llega del fetch.
const OB_HINT = 'pl_ob_hint__'
function readHint(userId) {
  try { return window.localStorage.getItem(OB_HINT + userId) } catch (_) { return null }
}
function writeHint(userId, completed) {
  try { window.localStorage.setItem(OB_HINT + userId, String(!!completed)) } catch (_) {}
}
export function clearAllProfileHints() {
  try {
    const rm = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith(OB_HINT)) rm.push(k)
    }
    rm.forEach((k) => window.localStorage.removeItem(k))
  } catch (_) {}
}

const useAuthStore = create((set, get) => ({
  user: null,
  profile: null,
  loading: true,
  // bug#3: ¿hemos resuelto el perfil del usuario ACTUAL? El gate de routing NO decide
  // hasta que esto es true (evita caer a la app con perfil ajeno/ausente).
  profileResolved: false,

  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),
  clearSession: () => set({ user: null, profile: null, profileResolved: false }),

  fetchProfile: async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    // bug#3 punto 1: SIEMPRE setear (data ?? null) — nunca retener el perfil anterior.
    set({ profile: data ?? null, profileResolved: true })
    if (data) writeHint(userId, data.onboarding_completed)
  },

  // bug#3 WOW: resolución instantánea para usuario conocido (hint scopeado por uid).
  // Perfil mínimo solo-routing; el perfil completo lo trae fetchProfile en background.
  hydrateProfileHint: (userId) => {
    const h = readHint(userId)
    if (h !== null) {
      set({ profile: { id: userId, onboarding_completed: h === 'true' }, profileResolved: true })
      return true
    }
    return false
  },

  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    if (data?.user) {
      await get().fetchProfile(data.user.id)
      set({ user: data.user })
      ph()?.capture('user_signed_in')
    }
    return data
  },

  signUp: async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } }
    })
    if (error) throw error
    ph()?.capture('user_signed_up')
    return data
  },

  signOut: async () => {
    const _ph = ph()
    if (_ph) { _ph.capture('user_signed_out'); _ph.reset() }
    await supabase.auth.signOut()
    // QA-E2E bug#2/#3: limpiar TODO el estado de usuario que sobrevive en el navegador
    // (draft de onboarding + hints de perfil scopeados) para que la siguiente cuenta
    // nazca limpia y no herede el routing del anterior.
    try { clearAllOnboardingV2Drafts() } catch (_) {}
    try { clearAllProfileHints() } catch (_) {}
    set({ user: null, profile: null, profileResolved: false })
  },

  updateProfile: async (updates) => {
    const { user } = get()
    if (!user) return
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id)
      .select()
      .single()
    if (error) throw error
    set({ profile: data })
    return data
  }
}))

export default useAuthStore
